import { Inject, Injectable, InjectionToken, OnDestroy, Optional } from '@angular/core';
import { BehaviorSubject, fromEvent, interval, Observable, Subscription } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { switchMap } from 'rxjs/operators';
import { Logger } from './logger.service';
/**
 * Instance of this interface is used to report current connection status.
 */
export interface NetworkConnectionState {
  /**
   * "True" if browser has network connection. Determined by Window objects "online" / "offline" events.
   */
  hasNetworkConnection: boolean;
  /**
   * "True" if browser has Internet access. Determined by heartbeat system which periodically makes request to heartbeat Url.
   */
  hasInternetAccess: boolean;
}

/**
 * Instance of this interface could be used to configure "ConnectionService".
 */
export interface NetworkConnectionServiceOptions {
  /**
   * Controls the Internet connectivity heartbeat system. Default value is 'true'.
   */
  enableHeartbeat?: boolean;
  /**
   * Url used for checking Internet connectivity, heartbeat system periodically makes "HEAD" requests to this URL to determine Internet
   * connection status. Default value is "//internethealthtest.org".
   */
  heartbeatUrl?: string;
  /**
   * Interval used to check Internet connectivity specified in milliseconds. Default value is "30000".
   */
  heartbeatInterval?: number;
  /**
   * Interval used to retry Internet connectivity checks when an error is detected (when no Internet connection). Default value is "1000".
   */
  heartbeatRetryInterval?: number;
  /**
   * HTTP method used for requesting heartbeat Url. Default is 'head'.
   */
  requestMethod?: 'get' | 'post' | 'head' | 'options';
}

/**
 * InjectionToken for specifing ConnectionService options.
 */
export const NetworkConnectionServiceOptionsToken: InjectionToken<NetworkConnectionServiceOptions> = new InjectionToken(
  'ConnectionServiceOptionsToken'
);

export const DEFAULT_CONNECTION_STATE: NetworkConnectionState = {
  hasInternetAccess: true,
  hasNetworkConnection: true,
};

export const DEFAULT_HEART_BEAT_INTERVAL = 1000;
export const DEFAULT_HEART_BEAT_URL = window.location.origin;
export const DEFAULT_HEART_BEAT_RETRY_INTERVAL = 1000;

export enum HTTP_REQUEST_METHODS {
  HEAD = 'head',
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  OPTIONS = 'options',
}

export const DEFAULT_OPTIONS: NetworkConnectionServiceOptions = {
  enableHeartbeat: false,
  heartbeatUrl: DEFAULT_HEART_BEAT_URL,
  heartbeatInterval: DEFAULT_HEART_BEAT_INTERVAL,
  heartbeatRetryInterval: 1000,
  requestMethod: HTTP_REQUEST_METHODS.HEAD,
};

@Injectable({
  providedIn: 'root',
})
export class NetworkConnectionService implements OnDestroy {
  private currentState: NetworkConnectionState = DEFAULT_CONNECTION_STATE;
  private serviceOptions: NetworkConnectionServiceOptions = DEFAULT_OPTIONS;

  private subscription: Subscription = new Subscription();
  private httpSubscription: Subscription = new Subscription();

  public stateChanged$ = new BehaviorSubject<NetworkConnectionState>(DEFAULT_CONNECTION_STATE);

  private isRequestInProgress = false; // Flag to track ongoing requests

  constructor(
    private http: HttpClient,
    @Inject(NetworkConnectionServiceOptionsToken) @Optional() options: NetworkConnectionServiceOptions
  ) {
    // TODO: Token useValue in providers not working.
    this.serviceOptions = { ...DEFAULT_OPTIONS, ...options };
    this.checkNetworkState();

    if (this.serviceOptions.enableHeartbeat) {
      this.checkInternetState();
    }
  }

  private checkNetworkState() {
    console.log('checkNetworkState called');
    this.subscription.add(
      fromEvent(window, 'online').subscribe(() => {
        this.currentState.hasNetworkConnection = true;
        this.checkInternetState();
        Logger.internetState = true;
        this.publishState();
      })
    );

    this.subscription.add(
      fromEvent(window, 'offline').subscribe(() => {
        this.currentState.hasNetworkConnection = false;
        this.checkInternetState();
        this.publishState();
        Logger.internetState = false;
      })
    );
  }

  private checkInternetState() {
    if (this.serviceOptions.enableHeartbeat) {
      const url = new URL(this.serviceOptions.heartbeatUrl || DEFAULT_HEART_BEAT_URL);
      //url.searchParams.set('rand', new Date().getTime().toString());
      this.subscription = interval(this.serviceOptions.heartbeatInterval)
        .pipe(
          switchMap(async () => {
            if (!this.isRequestInProgress && !document.hidden) {
              this.isRequestInProgress = true;
              this.http
                .skipErrorHandler()
                .disableLoader()
                .head(url.toString(), {
                  responseType: 'text',
                  headers: new HttpHeaders({ hideMonitor: 'true' }),
                })
                .subscribe({
                  next: (_: any) => {
                    this.currentState.hasInternetAccess = true;
                    this.publishState();
                    this.isRequestInProgress = false;
                  },

                  error: (_: any) => {
                    this.currentState.hasInternetAccess = false;
                    this.publishState();
                    this.isRequestInProgress = false;
                  },
                });
            }
          })
        )
        .subscribe((res) => {});
    } else {
      this.currentState.hasInternetAccess = false;
      this.publishState();
    }
  }

  private publishState() {
    this.stateChanged$.next(this.currentState);
  }

  /**
   * Monitor Network & Internet connection status by subscribing to this observer. If you set "reportCurrentState" to "false" then
   * function will not report current status of the connections when initially subscribed.
   * @param reportCurrentState Report current state when initial subscription. Default is "true"
   */
  public monitor(options?: NetworkConnectionServiceOptions): Observable<NetworkConnectionState> {
    if (options) {
      this.serviceOptions = { ...this.serviceOptions, ...options };
    }
    if (this.serviceOptions.enableHeartbeat) {
      this.checkInternetState();
    }
    return this.stateChanged$;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
